import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from 'react';

import { ArticleWithRelations, RequestWithRelations } from '@/models/request';
import { useCurrentSession } from '@/services/auth';

import {
  ArticleErrors,
  ArticleErrorsFilter,
  computeArticleErrors,
  FilteredArticleErrors,
  recursivelyFilterErrors,
} from './errors';
import { getArticleIam } from './iam';
import { RequestArticleContextData } from './interfaces';
import { useRequestContext } from './RequestContext';

type PriceUpdateInProgress = {
  actionId?: string;
  type: 'workshop' | 'organization';
  articleExchangeId?: string;
  sparePartId?: string;
};

export interface ArticleContextData {
  request: RequestWithRelations;
  article: ArticleWithRelations;
  view: RequestArticleContextData['view'];
  initialConditionFormData?: RequestArticleContextData['initialConditionFormData'];
  qualityControlFormData?: RequestArticleContextData['qualityControlFormData'];
  productAttributes?: RequestArticleContextData['productAttributes'];
  proofOfPurchaseMedia: RequestArticleContextData['proofOfPurchaseMedia'];
  proofOfAuthenticityMedia?: RequestArticleContextData['proofOfAuthenticityMedia'];
  unfilteredErrors: ArticleErrors | null;
  errors: FilteredArticleErrors;
  hasError: boolean;
  state: {
    customsPanel: {
      isOpen: boolean;
      setIsOpen: Dispatch<SetStateAction<boolean>>;
    };
    infoPanel: {
      isOpen: boolean;
      setIsOpen: Dispatch<SetStateAction<boolean>>;
    };
    priceUpdates: {
      updatesInProgress: PriceUpdateInProgress[];
      setUpdatesInProgress: Dispatch<SetStateAction<PriceUpdateInProgress[]>>;
    };
    errors: {
      check: (errorsFilter: ArticleErrorsFilter | null) => FilteredArticleErrors;
    };
  };
  iam: ReturnType<typeof getArticleIam>;
}

const ArticleContext = createContext({} as ArticleContextData);

export const ArticleContextProvider = ({
  article,
  children,
}: {
  article: ArticleWithRelations;
  children: ReactNode;
}) => {
  const { request, articles, view: requestView } = useRequestContext();
  const { currentSession } = useCurrentSession();

  const articleContextData = articles.find((a) => a.articleId === article.id)!;

  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
  const [isCustomsPanelOpen, setCustomsPanelOpen] = useState(false);
  const [priceUpdatesInProgress, setPriceUpdatesInProgress] = useState<PriceUpdateInProgress[]>([]);

  const articleState = {
    customsPanel: {
      isOpen: isCustomsPanelOpen,
      setIsOpen: setCustomsPanelOpen,
    },
    infoPanel: {
      isOpen: isInfoPanelOpen,
      setIsOpen: setIsInfoPanelOpen,
    },
    priceUpdates: {
      updatesInProgress: priceUpdatesInProgress,
      setUpdatesInProgress: setPriceUpdatesInProgress,
    },
  };

  const unfilteredErrors = computeArticleErrors({
    article,
    currentSession: currentSession!,
    proofOfPurchaseMedia: articleContextData.proofOfPurchaseMedia,
    proofOfAuthenticityMedia: articleContextData.proofOfAuthenticityMedia,
    initialConditionFormData: articleContextData.initialConditionFormData,
    qualityControlFormData: articleContextData.qualityControlFormData,
    productAttributes: articleContextData.productAttributes,
    requestView,
    articleState,
  });

  const [errorsFilter, setErrorsFilter] = useState<ArticleErrorsFilter | null>(null);

  const errors = unfilteredErrors
    ? recursivelyFilterErrors(unfilteredErrors, errorsFilter ?? {})
    : {
        hasError: false,
      };

  const iam = getArticleIam(article);

  return (
    <ArticleContext
      value={{
        request,
        article,
        ...articleContextData,
        unfilteredErrors,
        errors,
        hasError: errors.hasError ?? false,
        state: {
          ...articleState,
          errors: {
            check(errorsFilter: ArticleErrorsFilter | null) {
              setErrorsFilter(errorsFilter);

              // Directly returns the filtered errors, as the context hasn't re-rendered yet, so the state is not updated
              return unfilteredErrors
                ? recursivelyFilterErrors(unfilteredErrors, errorsFilter ?? {})
                : {
                    hasError: false,
                  };
            },
          },
        },
        iam,
      }}
    >
      {children}
    </ArticleContext>
  );
};

export const useArticleContext = () => useContext(ArticleContext);
